import React, { useState } from 'react';
import useAsyncEffect from 'use-async-effect';
import { FiSend, FiUser, FiX, FiLoader, FiShield } from 'react-icons/fi';
import api from '../../../api/api';

const PLACEHOLDER = '{{content_placeholder}}';
const AGREED_OPTIONS = ['They asked us in person or by phone', 'They asked us by email', 'They signed up on the website', 'They ticked a box on a form'];
const escapeHtml = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

interface Props {
    combinedTicker: string;
    holder: { holderId: number | string; name: string; email: string; balance: number };
    onClose: () => void;
    onSent: () => void;
}

const SendWelcomeEmailModal: React.FC<Props> = ({ combinedTicker, holder, onClose, onSent }) => {
    const [template, setTemplate] = useState<{ html: string; subject: string } | null>(null);
    const [error, setError] = useState('');
    const [email, setEmail] = useState(holder.email);
    const [line, setLine] = useState('');
    const [addToContacts, setAddToContacts] = useState(true);
    const [permission, setPermission] = useState(false);
    const [agreed, setAgreed] = useState(AGREED_OPTIONS[0]);
    const [sending, setSending] = useState(false);

    useAsyncEffect(async (isMounted: () => boolean) => {
        const result = await api().getWelcomeEmailTemplate(combinedTicker);
        if (!isMounted()) return;
        if (result.success) setTemplate({ html: result.html, subject: result.subject });
        else setError(result.errorMessage);
    }, [combinedTicker]);

    const hasSlot = !!template?.html.includes(PLACEHOLDER);
    const lineNeedsSlot = !!template && !hasSlot && !!line.trim();

    const send = async () => {
        if (!template) return;
        setSending(true);
        setError('');
        const personal = line.trim() ? `<p style="margin: 0;">${escapeHtml(line.trim())}</p>` : '';
        const html = template.html.split(PLACEHOLDER).join(personal);
        const result = await api().sendWelcomeEmail(combinedTicker, email.trim(), holder.name, template.subject, html);
        setSending(false);
        if (result.success) onSent();
        else setError(result.errorMessage);
    };

    const canSend = !!template && !sending && permission && !lineNeedsSlot && /\S+@\S+\.\S+/.test(email);

    return <div className="modal-overlay" onClick={onClose}>
        <div className="send-welcome-email-modal" role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
                <span className="icon-tile"><FiSend /></span>
                <div>
                    <h2>Send a Welcome Email</h2>
                    <p>Invites this holder to the Website Hub so you can email them directly</p>
                </div>
                <button type="button" className="close" aria-label="Close" onClick={onClose}><FiX /></button>
            </div>

            <div className="modal-body">
                <div className="col">
                    <div className="holder-card">
                        <span className="icon-tile"><FiUser /></span>
                        <div>
                            <div className="name">{holder.name}</div>
                            <div className="sub">{holder.email} · {holder.balance.toLocaleString()} shares</div>
                        </div>
                    </div>

                    <label className="field">
                        <span className="label">Send to <em>· email from the registry</em></span>
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} disabled={sending} />
                    </label>

                    <label className="field">
                        <span className="label">Template</span>
                        <select disabled><option>Welcome Email (system template)</option></select>
                    </label>

                    <label className="field">
                        <span className="label">Add a personal line <em>(optional)</em></span>
                        <textarea rows={3} value={line} onChange={e => setLine(e.target.value)} disabled={sending} />
                        {lineNeedsSlot && <span className="hint">The Welcome Email template has no {PLACEHOLDER}, so a personal line can’t be added.</span>}
                    </label>
                </div>

                <div className="col">
                    {/* ponytail: sending always subscribes; this box is informational until an opt-out exists */}
                    <label className={`check-card${addToContacts ? ' on' : ''}`}>
                        <input type="checkbox" checked={addToContacts} onChange={e => setAddToContacts(e.target.checked)} disabled={sending} />
                        <div>
                            <div className="title">Add them to Contacts as well</div>
                            <div className="sub">So they receive future announcement emails</div>
                        </div>
                    </label>

                    <div className="permission">
                        <div className="head"><FiShield /> Permission to email this person <span className="pill required">Required</span></div>
                        <label className={`check-card${permission ? ' on' : ''}`}>
                            <input type="checkbox" checked={permission} onChange={e => setPermission(e.target.checked)} disabled={sending} />
                            <div>
                                <div className="title">I confirm this person has agreed to receive investor emails.</div>
                                <div className="sub">You can’t add someone who has not agreed. Adding people without permission breaches the Spam Act 2003 (Cth).</div>
                            </div>
                        </label>
                        <label className="field">
                            <span className="label">How did they agree?</span>
                            <select value={agreed} onChange={e => setAgreed(e.target.value)} disabled={sending}>
                                {AGREED_OPTIONS.map(o => <option key={o}>{o}</option>)}
                            </select>
                        </label>
                    </div>
                </div>

                {error && <p className="error">{error}</p>}
            </div>

            <div className="modal-footer">
                <button type="button" className="link" onClick={onClose} disabled={sending}>Cancel</button>
                <button type="button" className="btn-primary" onClick={send} disabled={!canSend}>
                    {sending ? <FiLoader className="spin" /> : <FiSend />} {sending ? 'Sending…' : 'Send Welcome Email'}
                </button>
            </div>
        </div>
    </div>;
};

export default SendWelcomeEmailModal;
